import React, { useState } from 'react';
import WorkspaceLayout from '../components/Workspace/WorkspaceLayout';

interface ImpactFunction {
  id: string;
  name: string;
  department: string;
  owner: string;
  rto: string;
  rpo: string;
  hourlyLoss: number;
  priority: 'Tier 1 - Mission Critical' | 'Tier 2 - Essential' | 'Tier 3 - Non-Critical';
  dependencies: string[];
}

const IMPACT_FUNCTIONS: ImpactFunction[] = [
  {
    id: '1',
    name: 'Payroll processing',
    department: 'Finance',
    owner: 'A. Okafor',
    rto: '24h',
    rpo: '4h',
    hourlyLoss: 12500,
    priority: 'Tier 1 - Mission Critical',
    dependencies: ['Banking API', 'Core ERP', 'HR Portal'],
  },
  {
    id: '2',
    name: 'Customer support line',
    department: 'Operations',
    owner: 'J. Tremblay',
    rto: '4h',
    rpo: '1h',
    hourlyLoss: 8400,
    priority: 'Tier 1 - Mission Critical',
    dependencies: ['VoIP PBX (Single-vendor)', 'CRM Database'],
  },
  {
    id: '3',
    name: 'Order fulfilment',
    department: 'Logistics',
    owner: 'M. Singh',
    rto: '8h',
    rpo: '2h',
    hourlyLoss: 18200,
    priority: 'Tier 1 - Mission Critical',
    dependencies: ['Warehouse WMS', 'Carrier API', 'Label Printers'],
  },
  {
    id: '4',
    name: 'Patient records access',
    department: 'Clinical IT',
    owner: 'R. Bello',
    rto: '1h',
    rpo: '15m',
    hourlyLoss: 25000,
    priority: 'Tier 1 - Mission Critical',
    dependencies: ['EMR Cluster', 'Auth SSO', 'Encrypted Storage'],
  },
  {
    id: '5',
    name: 'Vendor payments',
    department: 'Finance',
    owner: 'A. Okafor',
    rto: '48h',
    rpo: '12h',
    hourlyLoss: 3100,
    priority: 'Tier 2 - Essential',
    dependencies: ['AP Ledger', 'SWIFT Connector'],
  },
  {
    id: '6',
    name: 'Email & collaboration',
    department: 'IT',
    owner: 'K. Lavoie',
    rto: '2h',
    rpo: '30m',
    hourlyLoss: 6200,
    priority: 'Tier 2 - Essential',
    dependencies: ['Google Workspace / M365', 'DNS Servers'],
  },
];

export const ImpactAnalysis: React.FC = () => {
  const [filterTier, setFilterTier] = useState<string>('all');
  const [selectedFunction, setSelectedFunction] = useState<ImpactFunction | null>(null);

  const filtered = IMPACT_FUNCTIONS.filter((fn) => {
    if (filterTier === 'tier1') return fn.priority.includes('Tier 1');
    if (filterTier === 'tier2') return fn.priority.includes('Tier 2');
    return true;
  });

  const totalHourlyDowntime = IMPACT_FUNCTIONS.reduce((sum, fn) => sum + fn.hourlyLoss, 0);

  return (
    <WorkspaceLayout
      title="Business Impact Analysis"
      subtitle="Evaluated against ISO 22301:2019 Clause 8.2.2 requirements"
    >
      <div className="space-y-6">
        {/* Metric Overview Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              Total Hourly Downtime Cost
            </div>
            <div className="mt-2 text-2xl font-extrabold text-[#991B1B]">
              ${totalHourlyDowntime.toLocaleString()}/hr
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Aggregated loss across all mission-critical services
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              Critical RTO Threshold
            </div>
            <div className="mt-2 text-2xl font-extrabold text-slate-900">1.0h</div>
            <div className="mt-1 text-xs text-slate-500">
              Shortest recovery requirement (Patient records access)
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              Single Point of Failures
            </div>
            <div className="mt-2 text-2xl font-extrabold text-[#D97706]">1 Detected</div>
            <div className="mt-1 text-xs text-slate-500">
              Flagged by Risk Agent in Customer support VoIP
            </div>
          </div>
        </div>

        {/* Functions Card */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 pb-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-base font-bold text-slate-900">Impact Assessment Matrix</h2>
              <p className="text-xs text-slate-500">
                Recovery Time Objectives (RTO) and Recovery Point Objectives (RPO)
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <select
                value={filterTier}
                onChange={(e) => setFilterTier(e.target.value)}
                className="px-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-800"
              >
                <option value="all">All Tiers (22)</option>
                <option value="tier1">Tier 1 - Mission Critical</option>
                <option value="tier2">Tier 2 - Essential</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-100 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  <th className="py-3 px-6">Function / Department</th>
                  <th className="py-3 px-4">Priority Tier</th>
                  <th className="py-3 px-4">RTO</th>
                  <th className="py-3 px-4">RPO</th>
                  <th className="py-3 px-4">Hourly Loss</th>
                  <th className="py-3 px-4">Key Dependencies</th>
                  <th className="py-3 px-6 text-right">Owner</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((fn) => (
                  <tr
                    key={fn.id}
                    onClick={() => setSelectedFunction(fn)}
                    className="hover:bg-slate-50/70 transition-colors cursor-pointer"
                  >
                    <td className="py-3.5 px-6">
                      <div className="font-semibold text-slate-900">{fn.name}</div>
                      <div className="text-[11px] text-slate-400">{fn.department}</div>
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                          fn.priority.includes('Tier 1')
                            ? 'bg-[#FEE2E2] text-[#991B1B]'
                            : 'bg-[#FEF3C7] text-[#B45309]'
                        }`}
                      >
                        {fn.priority}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 font-bold text-slate-900">{fn.rto}</td>
                    <td className="py-3.5 px-4 font-medium text-slate-700">{fn.rpo}</td>
                    <td className="py-3.5 px-4 font-bold text-[#DC2626]">
                      ${fn.hourlyLoss.toLocaleString()}/hr
                    </td>
                    <td className="py-3.5 px-4 text-slate-500">
                      <div className="flex flex-wrap gap-1">
                        {fn.dependencies.map((dep) => (
                          <span
                            key={dep}
                            className={`px-1.5 py-0.5 rounded text-[10px] ${
                              dep.includes('Single-vendor')
                                ? 'bg-amber-100 text-amber-800 font-semibold'
                                : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {dep}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3.5 px-6 text-right font-medium text-slate-700">
                      {fn.owner}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Function Detail Modal */}
        {selectedFunction && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl border border-slate-200">
              <div className="flex justify-between items-start pb-3 border-b border-slate-100">
                <div>
                  <h3 className="text-base font-bold text-slate-900">{selectedFunction.name}</h3>
                  <p className="text-xs text-slate-500">{selectedFunction.department} · Owner: {selectedFunction.owner}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedFunction(null)}
                  className="text-slate-400 hover:text-slate-600 text-lg font-bold"
                >
                  ✕
                </button>
              </div>

              <div className="mt-4 space-y-4 text-xs">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-[10px] uppercase font-bold text-slate-400">Recovery Time (RTO)</div>
                    <div className="mt-1 text-base font-extrabold text-slate-900">{selectedFunction.rto}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-[10px] uppercase font-bold text-slate-400">Recovery Point (RPO)</div>
                    <div className="mt-1 text-base font-extrabold text-slate-900">{selectedFunction.rpo}</div>
                  </div>
                </div>

                <div className="p-3 bg-rose-50/50 border border-rose-100 rounded-lg">
                  <div className="text-[10px] uppercase font-bold text-rose-800">Financial Impact</div>
                  <div className="mt-1 text-base font-extrabold text-rose-900">
                    ${selectedFunction.hourlyLoss.toLocaleString()}/hour
                  </div>
                  <div className="text-[11px] text-rose-700 mt-0.5">
                    Estimated direct revenue loss and customer SLA penalties
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-400 mb-1.5">Upstream & Downstream Dependencies</div>
                  <div className="space-y-1">
                    {selectedFunction.dependencies.map((dep) => (
                      <div key={dep} className="px-2.5 py-1.5 bg-slate-50 rounded border border-slate-100 text-slate-700 flex items-center justify-between">
                        <span>{dep}</span>
                        {dep.includes('Single-vendor') && (
                          <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Risk</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-3 border-t border-slate-100 flex justify-end">
                <button
                  type="button"
                  onClick={() => setSelectedFunction(null)}
                  className="px-4 py-1.5 bg-slate-900 text-white rounded-lg text-xs font-semibold hover:bg-slate-800"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </WorkspaceLayout>
  );
};

export default ImpactAnalysis;
